import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { customerApi, formatApiError } from '@/lib/api';
import { BottomNav } from '@/components/customer/BottomNav';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { LogOut, Trash2, ShieldCheck, FileText, LifeBuoy, RotateCcw, ChevronRight } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';

const LEGAL_ROWS = [
  { to: '/privacy', label: 'Privacy Policy', icon: ShieldCheck },
  { to: '/terms', label: 'Terms of Service', icon: FileText },
  { to: '/refunds', label: 'Refund & Cancellation', icon: RotateCcw },
  { to: '/support', label: 'Support & Contact', icon: LifeBuoy },
];

export default function Profile() {
  const { customer, logout, setCustomer } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState(customer?.name || '');
  const [phone, setPhone] = useState(customer?.phone || '');
  const [savingInfo, setSavingInfo] = useState(false);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [savingPassword, setSavingPassword] = useState(false);

  const [showDelete, setShowDelete] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleting, setDeleting] = useState(false);

  const saveInfo = async (e) => {
    e.preventDefault();
    setSavingInfo(true);
    try {
      const { data } = await customerApi.patch('/auth/customer/me', { name, phone });
      setCustomer(data.customer);
      toast.success('Profile updated');
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingInfo(false);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    setSavingPassword(true);
    try {
      await customerApi.post('/auth/customer/change-password', { currentPassword, newPassword });
      setCurrentPassword('');
      setNewPassword('');
      toast.success('Password changed');
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingPassword(false);
    }
  };

  const deleteAccount = async () => {
    setDeleting(true);
    try {
      await customerApi.delete('/auth/customer/me', { data: { password: deletePassword } });
      toast.success('Your account has been deleted');
      logout();
      navigate('/');
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-bone pb-24">
      <div className="max-w-md mx-auto px-6 pt-8 space-y-6">
        <h1 className="font-heading text-2xl font-700 text-charcoal">Profile</h1>

        <div className="rounded-xl bg-white border border-black/5 px-4 py-3 text-sm">
          <p className="text-muted-ink text-xs">Customer ID</p>
          <p className="font-medium text-charcoal" data-testid="profile-customer-code">
            {customer?.customerCode}
          </p>
        </div>

        <form onSubmit={saveInfo} className="bg-white rounded-2xl border border-black/5 shadow-sm p-5 space-y-4">
          <h2 className="font-heading font-semibold text-charcoal">Account information</h2>
          <div className="space-y-1.5">
            <Label htmlFor="name">Full name</Label>
            <Input id="name" data-testid="profile-name-input" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="phone">Phone</Label>
            <Input id="phone" data-testid="profile-phone-input" value={phone} onChange={(e) => setPhone(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>Email</Label>
            <Input value={customer?.email || ''} disabled data-testid="profile-email-input" />
          </div>
          <Button type="submit" disabled={savingInfo} data-testid="profile-save-btn" className="w-full h-10 rounded-full bg-saffron hover:bg-saffron-hover">
            {savingInfo ? 'Saving…' : 'Save Changes'}
          </Button>
        </form>

        <form onSubmit={changePassword} className="bg-white rounded-2xl border border-black/5 shadow-sm p-5 space-y-4">
          <h2 className="font-heading font-semibold text-charcoal">Change password</h2>
          <div className="space-y-1.5">
            <Label htmlFor="currentPassword">Current password</Label>
            <PasswordInput id="currentPassword" data-testid="profile-current-password-input" toggleTestId="profile-current-password-toggle" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="newPassword">New password</Label>
            <PasswordInput id="newPassword" minLength={6} data-testid="profile-new-password-input" toggleTestId="profile-new-password-toggle" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
          </div>
          <Button type="submit" disabled={savingPassword} data-testid="profile-change-password-btn" variant="outline" className="w-full h-10 rounded-full">
            {savingPassword ? 'Updating…' : 'Update Password'}
          </Button>
        </form>

        <div className="bg-white rounded-2xl border border-black/5 shadow-sm p-5 space-y-1">
          <h2 className="font-heading font-semibold text-charcoal px-1 pb-1">Legal &amp; support</h2>
          {LEGAL_ROWS.map((r) => (
            <Link
              key={r.to}
              to={r.to}
              data-testid={`profile-legal-${r.to.slice(1)}`}
              className="flex items-center gap-3 rounded-xl px-2 py-3 hover:bg-black/5 transition-colors duration-150"
            >
              <r.icon size={18} className="text-coriander shrink-0" />
              <span className="flex-1 text-sm text-charcoal">{r.label}</span>
              <ChevronRight size={16} className="text-muted-ink" />
            </Link>
          ))}
        </div>

        <Button
          variant="ghost"
          data-testid="profile-logout-btn"
          onClick={() => {
            logout();
            navigate('/cxlogin');
          }}
          className="w-full h-10 text-destructive hover:text-destructive hover:bg-destructive/10"
        >
          <LogOut size={16} className="mr-2" /> Log Out
        </Button>

        <div className="bg-white rounded-2xl border border-destructive/20 shadow-sm p-5 space-y-3">
          <h2 className="font-heading font-semibold text-charcoal">Delete account</h2>
          <p className="text-sm text-muted-ink">
            Permanently delete your account and personal data. This cannot be undone and any unused tiffin credits
            are forfeited.{' '}
            <Link to="/delete-account" className="text-coriander hover:underline">Learn more</Link>.
          </p>
          <Button
            variant="outline"
            data-testid="profile-delete-account-btn"
            onClick={() => setShowDelete(true)}
            className="w-full h-10 rounded-full border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
          >
            <Trash2 size={16} className="mr-2" /> Delete My Account
          </Button>
        </div>
      </div>

      <Dialog open={showDelete} onOpenChange={(o) => { setShowDelete(o); if (!o) setDeletePassword(''); }}>
        <DialogContent data-testid="delete-account-dialog">
          <DialogHeader>
            <DialogTitle>Delete your account?</DialogTitle>
            <DialogDescription>
              This permanently removes your name, email, and phone, and deactivates your account. Enter your
              password to confirm.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="deletePassword">Password</Label>
            <PasswordInput
              id="deletePassword"
              data-testid="delete-account-password-input"
              toggleTestId="delete-account-password-toggle"
              value={deletePassword}
              onChange={(e) => setDeletePassword(e.target.value)}
              placeholder="Enter your password"
            />
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" data-testid="delete-account-cancel-btn" onClick={() => setShowDelete(false)}>
              Cancel
            </Button>
            <Button
              data-testid="delete-account-confirm-btn"
              disabled={deleting || !deletePassword}
              onClick={deleteAccount}
              className="bg-destructive hover:bg-destructive/90 text-white"
            >
              {deleting ? 'Deleting…' : 'Delete permanently'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <BottomNav />
    </div>
  );
}
